import { useEffect } from 'react'
import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { useOptimisticTipsStore } from 'use-towns-client'

import { getCurrencyBreakdown } from './useFormattedNormalizedTipAmount'
import { useUserTips } from './useUserTips'

export interface TipsReceived {
    count: number
    ethAmount: bigint
    usdcAmount: bigint
}

export const useOptimisticTips = (userId: string | undefined): TipsReceived => {
    const { data: userTips } = useUserTips(userId)
    const usdcMetadata = useUSDCMetadata()

    const optimisticCount = useOptimisticTipsStore((state) =>
        userId ? state.getTipsForRecipient(userId) : 0,
    )
    const optimisticAmounts = useOptimisticTipsStore((state) =>
        userId ? state.getAmountsForRecipient(userId) : {},
    )
    const highWaterMark = useOptimisticTipsStore((state) =>
        userId
            ? state.getHighWaterMarkForUser(userId)
            : { tipCount: 0, ethAmount: '0', usdcAmount: '0' },
    )

    // Calculate count
    let currentCount = optimisticCount
    if (userTips?.receivedCount) {
        const actualCount = Object.values(userTips.receivedCount).reduce((total, count) => {
            return total + (parseInt(count, 10) || 0)
        }, 0)
        currentCount = actualCount + optimisticCount
    }

    // Calculate amount - break down by currency (server + optimistic)
    let currentEthAmount = 0n
    let currentUsdcAmount = 0n

    // Server tips
    if (userTips?.received) {
        const { ethAmount, usdcAmount } = getCurrencyBreakdown(userTips.received)
        currentEthAmount = ethAmount
        currentUsdcAmount = usdcAmount
    }

    // Optimistic tips
    const { ethAmount: optimisticEthAmount, usdcAmount: optimisticUsdcAmount } =
        getCurrencyBreakdown(optimisticAmounts)
    currentEthAmount += optimisticEthAmount
    currentUsdcAmount += optimisticUsdcAmount

    // Apply high water mark to preserve peak amounts
    const highWaterMarkEthAmount = BigInt(highWaterMark.ethAmount || '0')
    const highWaterMarkUsdcAmount = BigInt(highWaterMark.usdcAmount || '0')
    const finalEthAmount =
        currentEthAmount > highWaterMarkEthAmount ? currentEthAmount : highWaterMarkEthAmount
    const finalUsdcAmount =
        currentUsdcAmount > highWaterMarkUsdcAmount ? currentUsdcAmount : highWaterMarkUsdcAmount
    const finalCount = Math.max(currentCount, highWaterMark.tipCount)

    useEffect(() => {
        if (userId) {
            if (optimisticCount > 0 || currentEthAmount > 0n || currentUsdcAmount > 0n) {
                if (
                    finalCount > highWaterMark.tipCount ||
                    finalEthAmount > highWaterMarkEthAmount ||
                    finalUsdcAmount > highWaterMarkUsdcAmount
                ) {
                    useOptimisticTipsStore
                        .getState()
                        .updateHighWaterMarkForUser(
                            userId,
                            finalCount,
                            finalEthAmount,
                            finalUsdcAmount,
                        )
                }
            } else if (
                highWaterMark.tipCount > 0 ||
                highWaterMarkEthAmount > 0n ||
                highWaterMarkUsdcAmount > 0n
            ) {
                useOptimisticTipsStore.getState().clearHighWaterMarkForUser(userId)
            }
        }
    }, [
        userId,
        optimisticCount,
        currentEthAmount,
        currentUsdcAmount,
        finalCount,
        finalEthAmount,
        finalUsdcAmount,
        highWaterMark,
        highWaterMarkEthAmount,
        highWaterMarkUsdcAmount,
        usdcMetadata.address,
    ])

    return {
        count: finalCount,
        ethAmount: finalEthAmount,
        usdcAmount: finalUsdcAmount,
    }
}
